import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

function normalizeSurface(value) {
  const raw = String(value || "").trim().toUpperCase()
  if (!raw || raw === "ALL" || raw === "GENERAL" || raw === "TOOTH") return ""
  return raw
}

function buildPlanTitleFromItems(items) {
  if (!items.length) return ""
  if (items.length === 1) {
    const first = items[0]
    const toothLabel = first.tooth ? ` Â· Pieza ${first.tooth}` : ""
    return `${first.treatmentName || "Plan de tratamiento"}${toothLabel}`
  }
  return `Plan de tratamiento Â· ${items.length} conceptos`
}

async function parseJsonSafe(res) {
  try {
    const text = await res.text()
    if (!text) return null
    return JSON.parse(text)
  } catch {
    return null
  }
}

export default function useTreatmentPlanDraft({
  clinicId,
  patientIdFromUrl,
  effectivePatientId,
  treatmentsSorted,
  loadTreatments,
  loadPlans,
  setSelectedPatientId
}) {
  const prefillConsumedRef = useRef(false)

  const [createModeOpen, setCreateModeOpen] = useState(false)
  const [creatingPlan, setCreatingPlan] = useState(false)

  const [planHeader, setPlanHeader] = useState({
    title: "",
    notes: ""
  })

  const [itemDraft, setItemDraft] = useState({
    treatmentId: "",
    tooth: "",
    surface: "",
    condition: "",
    price: "",
    notes: ""
  })

  const [planItems, setPlanItems] = useState([])

  const [showQuickTreatmentForm, setShowQuickTreatmentForm] = useState(false)
  const [savingQuickTreatment, setSavingQuickTreatment] = useState(false)
  const [quickTreatmentForm, setQuickTreatmentForm] = useState({
    name: "",
    price: "",
    durationMinutes: "30",
    requiredSpecialty: "",
    recallDays: "",
    odontogramCondition: ""
  })

  const selectedTreatment = useMemo(() => {
    return (
      treatmentsSorted.find((item) => String(item.id) === String(itemDraft.treatmentId)) || null
    )
  }, [treatmentsSorted, itemDraft.treatmentId])

  useEffect(() => {
    if (!selectedTreatment) return

    setItemDraft((prev) => ({
      ...prev,
      price:
        prev.price !== ""
          ? prev.price
          : selectedTreatment.price != null
            ? String(selectedTreatment.price)
            : "",
      condition:
        prev.condition ||
        (Array.isArray(selectedTreatment.odontogramMappings) &&
        selectedTreatment.odontogramMappings.length
          ? String(selectedTreatment.odontogramMappings[0]?.condition || "")
              .trim()
              .toUpperCase()
          : "")
    }))
  }, [selectedTreatment])

  useEffect(() => {
    if (!clinicId) return
    if (!patientIdFromUrl) return
    if (prefillConsumedRef.current) return

    const rawDiagnosis = localStorage.getItem("odontoflow_plan_prefill_diagnosis")
    const rawOdontogram = localStorage.getItem("odontoflow_plan_prefill")
    const raw = rawDiagnosis || rawOdontogram

    if (!raw) return

    try {
      const parsed = JSON.parse(raw)

      if (String(parsed?.clinicId || "") !== String(clinicId)) return
      if (String(parsed?.patientId || "") !== String(patientIdFromUrl || effectivePatientId || "")) {
        return
      }

      const isDiagnosisSource =
        String(parsed?.source || "").trim().toUpperCase() === "INITIAL_DIAGNOSIS"

      const defaultTitle = isDiagnosisSource
        ? parsed?.title ||
          `Plan derivado de diagnÃ³stico${parsed?.tooth ? ` Â· Pieza ${parsed.tooth}` : ""}`
        : String(parsed?.definitionType || "").trim().toUpperCase() === "SURFACE"
          ? `Plan pieza ${parsed?.tooth || ""} Â· ${parsed?.surface || "O"}`
          : `Plan general pieza ${parsed?.tooth || ""}`

      const defaultNotes = isDiagnosisSource
        ? parsed?.notes ||
          `DiagnÃ³stico principal: ${parsed?.mainDiagnosis || "-"}\n` +
            `DiagnÃ³stico secundario: ${parsed?.secondaryDiagnosis || "-"}\n` +
            `Pieza: ${parsed?.tooth || "-"}\n` +
            `Superficie: ${parsed?.surface || "No aplica"}\n` +
            `Severidad: ${parsed?.severity || "-"}`
        : `Hallazgo enviado desde odontograma.\n` +
          `Pieza: ${parsed?.tooth || "-"}\n` +
          `Tipo: ${
            String(parsed?.definitionType || "").trim().toUpperCase() === "SURFACE"
              ? "Superficie"
              : "Diente completo"
          }\n` +
          `Superficie: ${
            String(parsed?.definitionType || "").trim().toUpperCase() === "SURFACE"
              ? parsed?.surface || "O"
              : "No aplica"
          }\n` +
          `CondiciÃ³n: ${parsed?.condition || "-"}`

      setSelectedPatientId((prev) => prev || String(parsed?.patientId || ""))
      setPlanHeader({
        title: defaultTitle,
        notes: defaultNotes
      })

      setItemDraft((prev) => ({
        ...prev,
        tooth: parsed?.tooth != null ? String(parsed.tooth) : "",
        surface: normalizeSurface(parsed?.surface),
        condition: String(parsed?.condition || parsed?.mainDiagnosis || "")
          .trim()
          .toUpperCase()
      }))

      setQuickTreatmentForm((prev) => ({
        ...prev,
        odontogramCondition: String(parsed?.condition || "")
          .trim()
          .toUpperCase()
      }))

      setCreateModeOpen(true)
      prefillConsumedRef.current = true

      localStorage.removeItem("odontoflow_plan_prefill")
      localStorage.removeItem("odontoflow_plan_prefill_diagnosis")
    } catch {
      // silencio intencional
    }
  }, [clinicId, patientIdFromUrl, effectivePatientId, setSelectedPatientId])

  const planDraftTotal = useMemo(() => {
    return planItems.reduce((acc, item) => acc + Number(item.price || 0), 0)
  }, [planItems])

  const addDraftItem = useCallback(() => {
    if (!itemDraft.treatmentId) {
      alert("Selecciona un tratamiento.")
      return
    }

    const treatment = treatmentsSorted.find(
      (item) => String(item.id) === String(itemDraft.treatmentId)
    )

    if (!treatment) {
      alert("El tratamiento seleccionado no existe en el catÃ¡logo.")
      return
    }

    const price =
      itemDraft.price !== "" ? Number(itemDraft.price) : Number(treatment.price || 0)

    const nextItem = {
      localId: `${Date.now()}-${Math.random()}`,
      treatmentId: String(treatment.id),
      treatmentName: treatment.name || "Tratamiento",
      tooth: itemDraft.tooth ? Number(itemDraft.tooth) : null,
      surface: normalizeSurface(itemDraft.surface) || null,
      condition: String(itemDraft.condition || "").trim().toUpperCase() || null,
      price: Number.isFinite(price) ? price : 0,
      notes: String(itemDraft.notes || "").trim() || null
    }

    setPlanItems((prev) => {
      const next = [...prev, nextItem]

      setPlanHeader((current) => ({
        ...current,
        title: current.title || buildPlanTitleFromItems(next)
      }))

      return next
    })

    setItemDraft({
      treatmentId: "",
      tooth: "",
      surface: "",
      condition: "",
      price: "",
      notes: ""
    })
  }, [itemDraft, treatmentsSorted])

  const removeDraftItem = useCallback((localId) => {
    setPlanItems((prev) => prev.filter((item) => item.localId !== localId))
  }, [])

  const clearDraft = useCallback(() => {
    localStorage.removeItem("odontoflow_plan_prefill")
    localStorage.removeItem("odontoflow_plan_prefill_diagnosis")

    prefillConsumedRef.current = false

    setPlanHeader({
      title: "",
      notes: ""
    })

    setItemDraft({
      treatmentId: "",
      tooth: "",
      surface: "",
      condition: "",
      price: "",
      notes: ""
    })

    setPlanItems([])

    setShowQuickTreatmentForm(false)

    setQuickTreatmentForm({
      name: "",
      price: "",
      durationMinutes: "30",
      requiredSpecialty: "",
      recallDays: "",
      odontogramCondition: ""
    })
  }, [])

  const handleQuickCreateTreatment = useCallback(async () => {
    try {
      if (!clinicId) {
        alert("No hay una clÃ­nica vÃ¡lida seleccionada.")
        return
      }

      if (!quickTreatmentForm.name.trim()) {
        alert("Escribe el nombre del tratamiento.")
        return
      }

      setSavingQuickTreatment(true)

      const res = await apiFetch(`${API_BASE}/treatments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          name: quickTreatmentForm.name.trim(),
          durationMinutes: Number(quickTreatmentForm.durationMinutes || 30),
          price: Number(quickTreatmentForm.price || 0),
          requiredSpecialty: quickTreatmentForm.requiredSpecialty || null,
          recallDays: quickTreatmentForm.recallDays
            ? Number(quickTreatmentForm.recallDays)
            : null,
          odontogramCondition: quickTreatmentForm.odontogramCondition || null
        })
      })

      const data = await parseJsonSafe(res)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el tratamiento.")
        return
      }

      if (quickTreatmentForm.odontogramCondition && data?.id) {
        try {
          await apiFetch(`${API_BASE}/treatments/assign-odontogram-condition`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              clinicId,
              treatmentId: data.id,
              condition: quickTreatmentForm.odontogramCondition
            })
          })
        } catch {
          // opcional
        }
      }

      await loadTreatments()

      const createdTreatmentId = String(data?.id || "")
      if (createdTreatmentId) {
        setItemDraft((prev) => ({
          ...prev,
          treatmentId: createdTreatmentId,
          price: data?.price != null ? String(data.price) : prev.price,
          condition:
            prev.condition ||
            String(quickTreatmentForm.odontogramCondition || "").trim().toUpperCase()
        }))
      }

      setShowQuickTreatmentForm(false)
      setQuickTreatmentForm({
        name: "",
        price: "",
        durationMinutes: "30",
        requiredSpecialty: "",
        recallDays: "",
        odontogramCondition: ""
      })

      alert("Tratamiento creado correctamente.")
    } catch {
      alert("Error al crear el tratamiento.")
    } finally {
      setSavingQuickTreatment(false)
    }
  }, [clinicId, quickTreatmentForm, loadTreatments])

  const createManualPlan = useCallback(async () => {
    try {
      if (!clinicId) {
        alert("No hay una clÃ­nica vÃ¡lida seleccionada.")
        return
      }

      if (!effectivePatientId) {
        alert("Selecciona un paciente para crear el plan.")
        return
      }

      if (!planItems.length) {
        alert("Agrega al menos un tratamiento al plan.")
        return
      }

      const title = String(planHeader.title || "").trim() || buildPlanTitleFromItems(planItems)

      if (!title) {
        alert("Escribe un tÃ­tulo para el plan.")
        return
      }

      setCreatingPlan(true)

      const payload = {
        clinicId,
        patientId: effectivePatientId,
        title,
        notes: String(planHeader.notes || "").trim() || undefined,
        items: planItems.map((item) => ({
          treatmentId: item.treatmentId,
          tooth: item.tooth != null ? Number(item.tooth) : undefined,
          surface: item.surface || undefined,
          condition: item.condition || undefined,
          price: item.price != null ? Number(item.price) : undefined,
          notes: item.notes || undefined
        }))
      }

      const res = await apiFetch(`${API_BASE}/appointments/treatment-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await parseJsonSafe(res)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el plan.")
        return
      }

      clearDraft()
      await loadPlans()
      setCreateModeOpen(false)

      alert("Plan creado correctamente.")
    } catch {
      alert("Error al crear el plan.")
    } finally {
      setCreatingPlan(false)
    }
  }, [clinicId, effectivePatientId, planItems, planHeader, clearDraft, loadPlans])

  return {
    createModeOpen,
    setCreateModeOpen,
    creatingPlan,
    planHeader,
    setPlanHeader,
    itemDraft,
    setItemDraft,
    planItems,
    showQuickTreatmentForm,
    setShowQuickTreatmentForm,
    savingQuickTreatment,
    quickTreatmentForm,
    setQuickTreatmentForm,
    planDraftTotal,
    addDraftItem,
    removeDraftItem,
    clearDraft,
    handleQuickCreateTreatment,
    createManualPlan
  }
}
